// English strings for P6-D (IMPORT-01/02, INVENTORY-03, POS-03/04/06, jobs DLQ recorder + schedules) (D-013: English only). Owned by that worker;
// spread into `en` as `en.imports`, so keys read `t("imports.…")`. Copy rules: DESIGN_SYSTEM §9.6.
// Sections: `imports` (IMPORT-01/02 screens), `transfers` / `batches` / `stockCheck` (INVENTORY-03),
// `registers` / `shifts` / `offline` (POS-04/06) and `qr` (POS-03).
export const importsStrings = {
  page: {
    title: "Import and export",
    lead: "Bring products, stock and customers in from a spreadsheet, or download them to edit and import again.",
    tabImport: "Import",
    tabExport: "Export",
    tabHistory: "History",
  },
  kinds: {
    products: "Products",
    edit_roundtrip: "Edit products (round-trip file)",
    inventory: "Stock counts",
    customers: "Customers",
    orders: "Past orders",
  },
  kindHelp: {
    products:
      "One row per product option. Rows with the same group key become one product with options (sizes, colours).",
    edit_roundtrip:
      "Use a products file you exported here. Only the cells you changed are saved; product and option IDs stay as they are.",
    inventory:
      "Set the counted stock for each item by item code, barcode or ID. Leave the location empty for the default location.",
    customers:
      "Add customers by phone number. A phone that is already a customer is skipped, never overwritten.",
  },
  file: {
    label: "Spreadsheet file (.csv)",
    help: "Save Excel sheets as a .csv file first. Up to 10 MB, read on this device.",
    choose: "Choose a file",
    reading: "Reading the file…",
    rows: { one: "{count} row", other: "{count} rows" },
    tooLarge: "This file is larger than 10 MB. Split it into smaller files.",
    empty: "This file has no rows under the header line.",
    unreadable: "This file couldn't be read. Save it as a .csv file and try again.",
  },
  mapping: {
    title: "Match the columns",
    lead: "Choose which column of your file fills each field. Columns we recognised are matched already.",
    sourceColumn: "Column in your file",
    ignore: "Don't import",
    required: "{field} needs a column.",
  },
  fields: {
    group_key: "Group key",
    title: "Title",
    description: "Description",
    status: "Status",
    channels: "Sales channels",
    tags: "Tags",
    tax_class: "Tax class",
    track_inventory: "Track stock",
    allow_backorder: "Sell when out of stock",
    option1_name: "Option 1 name",
    option1_value: "Option 1 value",
    option2_name: "Option 2 name",
    option2_value: "Option 2 value",
    option3_name: "Option 3 name",
    option3_value: "Option 3 value",
    sku: "Item code (SKU)",
    barcode: "Barcode",
    price: "Price (Rs)",
    compare_at_price: "Compare-at price (Rs)",
    cost_price: "Cost (Rs)",
    weight_g: "Weight (grams)",
    stock: "Stock",
    image_url: "Image link",
    image_alt: "Image description",
    variant_id: "Variant ID",
    product_id: "Product ID",
    product_version: "Product version",
    location: "Location code",
    on_hand: "Counted stock",
    phone: "Phone",
    name: "Name",
    email: "Email",
    note: "Note",
  },
  images: {
    label: "Fetch product images from their links",
    help: "Images are downloaded safely in the background and added to each product.",
  },
  preview: {
    title: "Check before importing",
    lead: "We checked every row. Nothing is saved until you start the import.",
    checking: "Checking rows {done} of {total}…",
    ok: { one: "{count} row is ready", other: "{count} rows are ready" },
    failed: { one: "{count} row has a problem", other: "{count} rows have a problem" },
    skipped: {
      one: "{count} row is already imported or unchanged",
      other: "{count} rows are already imported or unchanged",
    },
    changes: { one: "{count} cell will change", other: "{count} cells will change" },
    errorsCaption: "Rows with a problem",
    diffCaption: "Changes",
    row: "Row",
    field: "Field",
    problem: "Problem",
    from: "Now",
    to: "New",
    start: "Start import",
    starting: "Starting…",
  },
  progress: {
    title: "Importing",
    lead: "You can leave this page. The import continues in the background.",
    rows: "{done} of {total} rows",
    done: "Import finished",
    doneLead: "{ok} imported, {failed} with problems, {skipped} skipped.",
    cancelled: "Import cancelled",
    failed: "Import stopped",
    failedReasons: {
      dead_lettered:
        "Part of this file kept failing in the background, so the import stopped. Rows already imported are kept.",
    },
    rerun: "Run the rest again",
    rerunHelp: "Only the parts that did not finish run again. Rows already imported are skipped.",
    cancel: "Cancel import",
    errorFile: "Download the rows with problems",
    errorFileHelp:
      "Fix the rows in the file and import it again. Rows already imported are skipped.",
    another: "Import another file",
    images: "Images: {ok} added, {failed} could not be fetched",
  },
  history: {
    caption: "Recent imports",
    emptyTitle: "No imports yet",
    emptyBody: "Imports you start appear here with their results.",
    file: "File",
    kind: "Type",
    state: "Status",
    result: "Result",
    when: "Started",
    result_line: "{ok} ok · {failed} problems · {skipped} skipped",
  },
  states: {
    draft: "Draft",
    mapping: "Matching columns",
    dry_run: "Checked",
    running: "Importing",
    awaiting_review: "Waiting for review",
    done: "Done",
    failed: "Failed",
    cancelled: "Cancelled",
    queued: "Queued",
  },
  export: {
    lead: "Exports are prepared in the background and kept for 7 days. Downloading customer or order data asks you to verify it's you.",
    kinds: {
      products: "Products (to edit and import again)",
      inventory: "Stock by location",
      orders: "Orders",
      customers: "Customers",
    },
    start: "Prepare export",
    preparing: "Preparing…",
    ready: "Ready to download",
    download: "Download",
    rows: { one: "{count} row", other: "{count} rows" },
    caption: "Recent exports",
    emptyTitle: "No exports yet",
    pii: "Contains personal data",
    started: "We're preparing your export. It appears below when it's ready.",
  },
  errors: {
    notOpen: "This import is already running or finished. Start a new one.",
    empty: "Choose a file with rows first.",
    pending: "Background jobs aren't connected on this environment yet.",
  },

  // ---------------------------------------------------------------- INVENTORY-03
  transfers: {
    title: "Transfers",
    lead: "Move stock between your locations. Stock leaves the source when you send and arrives when you receive.",
    new: "New transfer",
    caption: "Transfers",
    emptyTitle: "No transfers yet",
    emptyBody:
      "Create a transfer to move stock from a warehouse to a counter, or between counters.",
    number: "Number",
    from: "From",
    to: "To",
    state: "Status",
    items: "Items",
    created: "Created",
    send: "Send",
    sending: "Sending…",
    receive: "Receive",
    receiving: "Receiving…",
    cancel: "Cancel transfer",
    sent: "Transfer {number} sent",
    received: "Transfer {number} received",
    created_toast: "Transfer {number} created",
    lines: "Items to move",
    addLine: "Add item",
    qty: "Quantity",
    receivedQty: "Received",
    receiveTitle: "Receive {number}",
    receiveLead: "Enter what arrived. Anything missing stays out of stock at both locations.",
    sameLocation: "Pick two different locations.",
    noLines: "Add at least one item.",
    availableAt: "{count} available at {location}",
    route: "Route",
    swap: "Swap from and to",
    searchPh: "Search by name, item code or barcode",
    noHits: "No tracked items match.",
    added: "Added",
    itemsEmpty: "Search above and add the items to move.",
    summary: { one: "{count} item", other: "{count} items" },
    units: { one: "{count} unit", other: "{count} units" },
    less: "One less",
    more: "One more",
    optional: "optional",
    notePh: "e.g. For the Dashain rush",
    sendHint: "Saved as a draft. Stock leaves {location} only when you send it.",
    close: "Close",
    cancelBtn: "Cancel",
    create: "Create transfer",
    notEnough: "Some items don't have that many available at {location}.",
    receivedOnly: "{count} received",
    moreItems: { one: "+{count} more item", other: "+{count} more items" },
    findItem: "Find an item",
    note: "Note",
    states: {
      draft: "Draft",
      in_transit: "In transit",
      received: "Received",
      cancelled: "Cancelled",
    },
    tabTransfers: "Transfers",
    tabBatches: "Batches",
    tabCheck: "Stock check",
  },
  batches: {
    title: "Batches and lots",
    lead: "Record stock that arrives with a lot code, cost and expiry. The item's cost is the average over its remaining lots.",
    new: "Receive a batch",
    caption: "Batches",
    emptyTitle: "No batches yet",
    emptyBody: "Batches you receive appear here with their cost and expiry date.",
    lot: "Lot code",
    item: "Item",
    location: "Location",
    qty: "Quantity",
    remaining: "Remaining",
    cost: "Unit cost (Rs)",
    expires: "Expires on",
    supplier: "Supplier reference",
    average: "Average cost {amount}",
    created: "Batch {lot} received",
    secBatch: "Batch details",
    change: "Change",
    onHandAt: "{count} on hand at {location}",
    generate: "Generate",
    lotPh: "e.g. LOT-2410",
    costShort: "Unit cost",
    supplierPh: "Supplier invoice or delivery note",
    pickItem: "Choose the item that arrived.",
    lotRequired: "Enter the lot code, or press Generate.",
    costRequired: "Enter the unit cost.",
    preview: "On hand at {location}: {from} → {to}",
    previewNone: { one: "Adds {count} unit", other: "Adds {count} units" },
    value: "Batch value {amount}",
    create: "Receive batch",
  },
  stockCheck: {
    title: "Stock check",
    lead: "Every night we check that each item's stock equals the sum of its stock movements. Differences are listed here and never changed automatically.",
    never: "The first check runs tonight at 00:00.",
    ranAt: "Last check {when}: {checked} items checked",
    clean: "Everything matched.",
    drifted: { one: "{count} item doesn't match", other: "{count} items don't match" },
    caption: "Items that don't match",
    onHand: "Stock",
    ledger: "Sum of movements",
  },

  // ---------------------------------------------------------------- POS-04 / POS-06
  registers: {
    title: "Registers and shifts",
    lead: "Each counter device is a register. Pair a device to sell cash offline, and open a shift to count the cash drawer.",
    caption: "Registers",
    new: "Add register",
    code: "Register code",
    codeHelp: "Up to 8 letters or digits. Printed on receipts, for example R1.",
    name: "Name",
    location: "Counter location",
    state: "Status",
    states: { unpaired: "Not paired", paired: "Paired", revoked: "Revoked" },
    thisDevice: "This device",
    pair: "Use this device",
    pairing: "Pairing…",
    paired: "This device is now register {code}",
    pairHelp: "Pairing asks you to verify it's you. The device keeps its own signing key.",
    revoke: "Revoke",
    revokeTitle: "Revoke register {code}?",
    revokeBody:
      "Offline sales signed by this register will no longer sync. Pair a device again to keep using it.",
    revoked: "Register {code} revoked",
    emptyTitle: "No registers yet",
    emptyBody: "Add a register for each counter device.",
    created: "Register {code} added",
  },
  shifts: {
    title: "Shift",
    none: "No shift is open on this register.",
    open: "Open shift",
    opening: "Opening…",
    float: "Opening cash (float)",
    openedAt: "Opened {when} with {amount}",
    expected: "Expected in drawer",
    sales: "Cash sales",
    payIn: "Cash in",
    payOut: "Cash out",
    drop: "Moved to safe",
    movement: "Cash movement",
    movementKind: "Type",
    amount: "Amount",
    note: "Note",
    add: "Record",
    close: "Close shift",
    closing: "Closing…",
    counted: "Counted cash",
    closeTitle: "Close the shift",
    closeLead:
      "Count the cash in the drawer. The difference from what we expect is recorded and posted to your books.",
    result: "Shift closed",
    over: "Over by {amount}",
    short: "Short by {amount}",
    exact: "The drawer matched exactly.",
    ledgerPending:
      "The difference is saved on the shift and posts to your books once accounting is connected.",
    pairFirst: "Pair this device as a register to open a shift.",
    history: "Recent shifts",
  },
  offline: {
    bannerPaired:
      "You're offline. Cash sales are saved on this device and sync when you're back online.",
    bannerUnpaired: "You're offline. Pair this device as a register to keep selling cash offline.",
    queued: {
      one: "{count} offline sale waiting to sync",
      other: "{count} offline sales waiting to sync",
    },
    syncing: "Syncing offline sales…",
    synced: { one: "{count} offline sale synced", other: "{count} offline sales synced" },
    conflicts: {
      one: "{count} sale needs a look: see the sync report",
      other: "{count} sales need a look: see the sync report",
    },
    rejected: { one: "{count} sale could not sync", other: "{count} sales could not sync" },
    cashOnly: "Offline, the counter takes cash only.",
    limitCount: "This device has {max} offline sales waiting. Connect to sync before selling more.",
    limitTime:
      "This device has been offline for more than {hours} hours. Connect to sync before selling more.",
    savedTitle: "Sale saved on this device",
    savedBody: "Receipt {number}. It syncs automatically when you're back online.",
    report: "Sync report",
    reportCaption: "Recent syncs",
    reportEmpty: "No offline sales have synced yet.",
    kinds: {
      price_drift: "Price changed while offline",
      stock_short: "Sold more than was in stock",
      tender_short: "Cash was short of the total",
      shift_closed: "The shift was already closed",
      late_sync: "Synced more than 8 hours later",
      receipt_number_reused: "Receipt number was used twice",
      unknown_item: "An item no longer exists",
      bad_signature: "The device signature didn't match",
      revoked_register: "The register was revoked",
      clock_ahead: "The device clock was ahead",
      op_reused: "The sale was sent by another register",
      manager_review: "Sent again after a manager's review",
    },
    held: {
      title: {
        one: "{count} offline sale needs a manager's review",
        other: "{count} offline sales need a manager's review",
      },
      body: "The server did not accept these sales because this register's key changed or was revoked. They stay on this device ({amount} taken) until a manager sends them again.",
      resend: "Re-sign and send",
      resendHelp:
        "Signs the sales again with this device's current key. You'll confirm it's you, and the review is recorded.",
      pairFirst:
        "Some sales belong to a register this device is no longer paired to. Pair this device to that register again to send them.",
      askManager: "Ask a manager to review them on this device.",
      sellHelp: "They are kept on this device. A manager can send them again from Registers.",
      open: "Open registers",
    },
    received: "Received",
    accepted: "Accepted",
    issues: "Issues",
  },
  qr: {
    referenceRequired: "Add the payment reference from the customer's app or bank message.",
    referenceHelp:
      "Counter QR is confirmed by the reference you read from the payment confirmation.",
    providerPending:
      "Counter QR through a payment provider isn't connected yet. Record the payment reference instead.",
  },
} as const;
